import React from 'react'
import styled from '@emotion/styled/macro'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import mq from 'mediaQuery'

import AddFavourite from '../AddFavourite/AddFavourite'
import { useAccount } from '../QueryAccount'
import ExpiryDate from './ExpiryDate'
import Loader from '../Loader'
import { humaniseName } from '../../utils/utils'
import Checkbox from '../Forms/Checkbox'
import { FaEye } from 'react-icons/fa'
import ImageTooltip from '../Tooltip/ImageTooltip'
import { ethers } from '@ace_domains/ui'

const CheckBoxContainer = styled('div')`
  margin: 5px;
`

const DomainContainer = styled(Link)`
  &:before {
    content: '';
    background: ${p => {
      switch (p.state) {
        case 'Yours':
          return 'linear-gradient(214deg, rgba(170,72,1,1) 4%, rgba(255,175,117,1) 45%, rgba(255,158,87,1) 64%, rgba(183,101,5,1) 82%, rgba(183,114,34,1) 100%)'
        case 'Open':
          return '#42E068'
        case 'Auction':
        case 'Reveal':
          return 'linear-gradient(-180deg, #42E068 0%, #52E5FF 100%)'
        case 'Owned':
          return '#CACACA'
        case 'Forbidden':
          return 'black'
        case 'NotYetAvailable':
          return 'red'
        default:
          return 'red'
      }
    }};
    width: 4px;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }
  color: #2b2b2b;
  padding: 20px;
  overflow: hidden;
  position: relative;
  background-color: inherit;
  background: ${({ percentdone }) =>
    percentdone
      ? `
  linear-gradient(to right, rgba(60, 63, 92, 0.1) 0%, rgba(82,229,255, 0.1) ${percentdone}%,#E8EBFC ${percentdone}%)`
      : '#c7bfff33'};
  border-radius: 6px;
  display: grid;
  height: auto;
  grid-template-columns: 1fr;
  grid-gap: 10px;
  align-items: center;
  font-size: 22px;
  margin-bottom: 4px;
  transition: 0.2s all;

  ${mq.medium`
    grid-template-columns: 1fr minmax(150px,350px) 100px 50px 50px;
    grid-template-rows: 39px;
  `}

  color: #2b2b2b;
  z-index: 1;
  box-shadow: 3px 4px 20px 5px rgba(60, 60, 93, 0.8);
  .label-container {
    display: flex;
  }

  &:visited {
    color: #2b2b2b;
  }
`

const RightContainer = styled('div')`
  display: flex;
  align-items: center;
`

const DomainName = styled('h2')`
  font-size: 18px;
  font-weight: 100;
  

  ${mq.medium`
    font-size: 28px;
  `}

  color: ${p => {
    switch (p.state) {
      case 'Yours':
      case 'Owned':
        return '#E8EBFC'
      default:
        return '#E8EBFC'
    }
  }};
`

const LabelContainer = styled('div')`
  margin-right: 20px;
  font-size: 16px;
  color: #b8860b;
  display: none;
  align-items: center;
`

const LabelText = styled('div')`
  font-size: 20px;
  color: darkgoldenrod;
`

const Label = ({ domain, isOwner }) => {
  const { t } = useTranslation()
  let text
  switch (domain.state) {
    case 'Open':
      text = t('singleName.domain.state.available')
      break
    case 'Auction':
      text = t('singleName.domain.state.auction')
      break
    case 'Owned':
      text = t('singleName.domain.state.owned')
      break
    default:
      text = t('singleName.domain.state.default')
  }

  if (isOwner) {
    text = t('singleName.domain.state.owned')
  }

  return (
    <LabelContainer className="label-container">
      <LabelText>{text}</LabelText>
    </LabelContainer>
  )
}

const Domain = ({
  domain,
  isSubDomain,
  className,
  isFavourite,
  loading,
  checkedBoxes = {},
  setCheckedBoxes,
  setSelectAll
}) => {
  if (loading) {
    return (
      <DomainContainer state={'Owned'} className={className} to="">
        <Loader />
      </DomainContainer>
    )
  }
  const nameArray = domain.name.split('.')
  const labelHash = ethers.utils.keccak256(ethers.utils.toUtf8Bytes(nameArray[0]))
  const tokenId = ethers.BigNumber.from(labelHash).toString();
  const account = useAccount()
  let isOwner = false
  if (!domain.available && domain.owner && parseInt(domain.owner, 16) !== 0) {
    isOwner =
      account &&
      domain.owner &&
      domain.owner.toLowerCase() === account.toLowerCase()
  }
  const percentdone = 0
  let expiryDate = domain.expiryDate
  if (domain.expiryTime) {
    expiryDate = parseInt(domain.expiryTime.getTime() / 1000)
  }
  return (
    <DomainContainer
      to={`/name/${domain.name}`}
      state={isOwner ? 'Yours' : domain.state}
      className={className}
      percentdone={percentdone}
      data-testid={`domain-${domain.name}`}
    >
      <DomainName state={isOwner ? 'Yours' : domain.state}>
        {humaniseName(domain.name)}
      </DomainName>
      <ExpiryDate expiryDate={expiryDate} name={domain.name} />
      <Label domain={domain} isOwner={isOwner} />
      <RightContainer>
        <AddFavourite
          domain={domain}
          isSubDomain={isSubDomain}
          isFavourite={isFavourite}
        />
      </RightContainer>
      <RightContainer>
        {expiryDate && (
          <>
            <ImageTooltip
              image={`https://${process.env.REACT_APP_METADATA_DOMAIN}/endurance/${process.env.REACT_APP_BASE_REGISTRAR}/${tokenId}/image`}
              offsetX={-250}
              offsetY={70}
            >
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                  onClick={e => {
                    e.preventDefault()
                    window.open(`https://${process.env.REACT_APP_METADATA_DOMAIN}/endurance/${process.env.REACT_APP_BASE_REGISTRAR}/${tokenId}/image`, "_blank")
                  }}
                >
                  <FaEye style={{
                    marginTop: "0.25em",
                    marginRight: "0.5em",
                    color: "#FFF",
                    width: "1.75em",
                    height: "1.75em",
                  }}/>
                </button>
              </div>
            </ImageTooltip>
          </>
        )}
      </RightContainer>
    </DomainContainer>
  )
}

export default Domain
